import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'wouter';
import { Pencil, Upload } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  ACCOUNT_SCOPE_LABELS,
  ACCOUNT_SUGGESTIONS_BY_PATH,
  ACCOUNT_TAXONOMY_BY_PATH,
  accountStatusTone,
  type AccountScope,
} from './accountModel';
import { useAccountRecord } from './AccountRecordContext';
import type { AccountCardKey } from './accountModel';

/**
 * AR-380 — the Account center's shared shapes.
 *
 * Three layouts and nothing else, matching the v13 prototype: a grid for
 * independent summary cards, a single column at a reading measure for record
 * cards, and a scrolling table. Everything else is a row.
 */

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

const TONE_CLASSES: Record<string, string> = {
  green:
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  amber:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  grey: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

export const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <span
    data-account-status={status}
    className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
      TONE_CLASSES[accountStatusTone(status)],
    )}
  >
    {status}
  </span>
);

export const Chip: React.FC<{ children: React.ReactNode; tone?: string }> = ({
  children,
  tone = 'grey',
}) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
      TONE_CLASSES[tone],
    )}
  >
    {children}
  </span>
);

export const ScopeChip: React.FC<{ scope: AccountScope }> = ({ scope }) => (
  <Chip>{ACCOUNT_SCOPE_LABELS[scope]}</Chip>
);

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

export interface RowProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** A chip beside the label — a status or a qualifier. */
  chip?: React.ReactNode;
  /** A control or door at the row's right edge, never joined to the value. */
  control?: React.ReactNode;
  /** A second line under the value — a door or a note. */
  extra?: React.ReactNode;
  last?: boolean;
  children?: React.ReactNode;
}

/** One record row: the label in ink, the value under it. */
export const Row: React.FC<RowProps> = ({
  label,
  value,
  chip,
  control,
  extra,
  last,
  children,
}) => (
  <div
    className={cn(
      'py-2.5 text-[15px]',
      !last && 'border-b border-gray-100 dark:border-gray-800',
      control && 'flex items-baseline gap-3',
    )}
  >
    <span className={cn(control && 'min-w-0 flex-1')}>
      <b className="font-semibold text-gray-900 dark:text-gray-100">{label}</b>
      {chip ? <span className="ml-2">{chip}</span> : null}
      {value !== undefined && value !== '' ? (
        <small className="block text-[14px] text-gray-500 dark:text-gray-400">
          {value}
        </small>
      ) : null}
      {extra ? (
        <small className="block text-[14px] text-gray-500 dark:text-gray-400">
          {extra}
        </small>
      ) : null}
      {children}
    </span>
    {control ? <span className="flex-none">{control}</span> : null}
  </div>
);

/** A row whose value is a list. */
export const RowList: React.FC<{
  label: React.ReactNode;
  items: readonly React.ReactNode[];
  extra?: React.ReactNode;
  last?: boolean;
}> = ({ label, items, extra, last }) => (
  <Row label={label} extra={extra} last={last}>
    <ul className="ml-5 mt-0.5 list-disc text-[14px] leading-relaxed text-gray-800 dark:text-gray-200">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </Row>
);

/** A switch in view mode that writes the record on click. */
export const SwitchRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  last?: boolean;
}> = ({ label, checked, onChange, disabled, last }) => (
  <div
    className={cn(
      'flex items-center gap-3 py-2.5',
      !last && 'border-b border-gray-100 dark:border-gray-800',
    )}
  >
    <span className="min-w-0 flex-1 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
      {label}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors',
        checked ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-0.5',
        )}
      />
    </button>
  </div>
);

/** A select in view mode that writes the record on change. */
export const SelectRow: React.FC<{
  label: string;
  value: string;
  options: readonly string[];
  onChange: (next: string) => void;
  last?: boolean;
}> = ({ label, value, options, onChange, last }) => (
  <div
    className={cn(
      'flex items-center gap-3 py-2.5',
      !last && 'border-b border-gray-100 dark:border-gray-800',
    )}
  >
    <span className="min-w-0 flex-1 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
      {label}
    </span>
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-none rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export const BandHead: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('mb-3 flex items-baseline gap-3', className)}>
    <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em] text-gray-600 dark:text-gray-400">
      {children}
    </span>
    <span className="flex-1 self-center border-t border-gray-200 dark:border-gray-700" />
  </div>
);

export const Grid: React.FC<{
  cols: 2 | 3;
  children: React.ReactNode;
  className?: string;
}> = ({ cols, children, className }) => (
  <div
    className={cn(
      'grid gap-3.5',
      cols === 2
        ? 'grid-cols-1 lg:grid-cols-2'
        : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
      className,
    )}
  >
    {children}
  </div>
);

/** A page of record cards at a reading measure. */
export const CardColumn: React.FC<{
  children: React.ReactNode;
  wide?: boolean;
  className?: string;
}> = ({ children, wide, className }) => (
  <div
    className={cn(
      'space-y-3.5',
      wide ? 'max-w-[1100px]' : 'max-w-[720px]',
      className,
    )}
  >
    {children}
  </div>
);

/** Record cards at their reading measure with a panel beside them. */
export const TwoColumnPage: React.FC<{
  main: React.ReactNode;
  side: React.ReactNode;
}> = ({ main, side }) => (
  <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,720px)_minmax(260px,1fr)]">
    <div className="space-y-3.5">{main}</div>
    <div>{side}</div>
  </div>
);

export const Table: React.FC<{
  columns: readonly string[];
  rows: readonly React.ReactNode[][];
  /** The notification matrix centres and narrows every column but the first. */
  matrix?: boolean;
  /**
   * The width below which the table scrolls instead of squeezing. Without one,
   * a table with a wide cell (the team row's Save / Cancel / Deactivate editor)
   * crushes the other columns to a character apiece rather than letting the
   * wrapper scroll.
   */
  minWidth?: number;
  /** Names the scrollable region for a screen reader. */
  label?: string;
}> = ({ columns, rows, matrix, minWidth, label }) => (
  /* A region that scrolls must be reachable by keyboard, so the wrapper takes
     focus — every cell here is text, so there is nothing else to tab to. */
  <div
    className="mt-0.5 overflow-x-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
    tabIndex={0}
    role={label ? 'region' : undefined}
    aria-label={label}
  >
    <table
      className="w-full border-collapse text-[14px]"
      style={minWidth ? { minWidth } : undefined}
    >
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th
              key={i}
              aria-label={c === '' ? 'Actions' : undefined}
              className={cn(
                'whitespace-nowrap border-b border-gray-200 px-2 py-1.5 text-left text-[11.5px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-gray-700 dark:text-gray-400',
                matrix && i > 0 && 'w-24 text-center',
              )}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            {r.map((cell, ci) => (
              <td
                key={ci}
                className={cn(
                  'border-b border-gray-100 px-2 py-2 align-middle text-gray-700 last:border-b-0 dark:border-gray-800 dark:text-gray-300',
                  ri === rows.length - 1 && 'border-b-0',
                  matrix && ci > 0 && 'text-center',
                )}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Muted: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span className={cn('text-[14px] text-gray-500 dark:text-gray-400', className)}>
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// The one card shape
// ---------------------------------------------------------------------------

export interface AccountCardProps {
  /** The card's id in the editing model. Omit for a card with no Edit. */
  cardKey?: AccountCardKey;
  name: string;
  /** The scope chip, a status chip, or both. */
  chip?: React.ReactNode;
  /** A line under the head that explains the card. */
  lead?: React.ReactNode;
  /** The rows the reader sees when the card is closed. */
  children: React.ReactNode;
  /** The form the reader edits when the card is open. */
  form?: React.ReactNode;
  /** A panel rendered beside the open form — the public profile preview. */
  side?: React.ReactNode;
  className?: string;
}

/**
 * One card shape everywhere: head, then either the rows or the form. A labelled
 * Edit sits in the head; while the card is open it is replaced by Save and
 * Cancel at the foot and the card takes the accent border.
 */
export const AccountCard: React.FC<AccountCardProps> = ({
  cardKey,
  name,
  chip,
  lead,
  children,
  form,
  side,
  className,
}) => {
  const { isOpen, openCard, saveCard, cancelCard, focusCard, setFocusCard } =
    useAccountRecord();
  const open = cardKey ? isOpen(cardKey) : false;
  const isPublic =
    cardKey === 'pub' || cardKey === 'spec' || cardKey === 'cta';

  // A search result names a card: bring it into view and mark it for a moment
  // so the eye finds it.
  const ref = useRef<HTMLDivElement>(null);
  const [flash, setFlash] = useState(false);
  const mine = focusCard !== null && focusCard === (cardKey ?? name);
  useEffect(() => {
    if (!mine) return;
    setFocusCard(null);
    ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 1600);
    return () => clearTimeout(timer);
  }, [mine, setFocusCard]);

  return (
    <div
      ref={ref}
      data-account-card={cardKey ?? name}
      className={cn(
        'rounded-lg border bg-white px-4 py-3.5 dark:bg-[#1A2021]',
        open
          ? 'border-orange-400 dark:border-orange-600'
          : 'border-gray-200 dark:border-gray-700',
        flash && 'outline outline-2 outline-offset-2 outline-orange-500',
        className,
      )}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
          {name}
        </span>
        <span className="flex flex-none items-center gap-2">
          {chip}
          {cardKey && !open ? (
            <button
              type="button"
              onClick={() => openCard(cardKey)}
              aria-label={`Edit ${name}`}
              className="inline-flex flex-none items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-0.5 text-[13px] text-gray-700 hover:border-orange-400 hover:text-orange-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
          ) : null}
        </span>
      </div>

      {lead && !open ? (
        <p className="-mt-1 mb-2 text-[14px] leading-snug text-gray-500 dark:text-gray-400">
          {lead}
        </p>
      ) : null}

      {open ? (
        <>
          {side ? (
            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
              <div className="space-y-3">{form}</div>
              <div className="hidden lg:block">{side}</div>
            </div>
          ) : (
            <div className="space-y-3">{form}</div>
          )}
          <div className="mt-3.5 flex gap-2">
            <button
              type="button"
              onClick={() => cardKey && saveCard(cardKey)}
              className="rounded-md bg-orange-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-800"
            >
              {isPublic ? 'Save draft' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => cardKey && cancelCard(cardKey)}
              className="rounded-md border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        children
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form fields — every one writes into the open card's draft by dot path
// ---------------------------------------------------------------------------

const FIELD_CLASS =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';

const LABEL_CLASS =
  'block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400 mb-1';

export const Hint: React.FC<{ children: React.ReactNode; error?: boolean }> = ({
  children,
  error,
}) => (
  <p
    className={cn(
      'mt-1 text-[13px]',
      error ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400',
    )}
  >
    {children}
  </p>
);

export const FieldPair: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 gap-x-3.5 gap-y-2.5 sm:grid-cols-2">{children}</div>
);

/** A text input bound to a draft path. Uncontrolled, so the caret never moves. */
export const TextField: React.FC<{
  path: string;
  label: string;
  placeholder?: string;
  hint?: React.ReactNode;
  disabled?: boolean;
}> = ({ path, label, placeholder, hint, disabled }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const id = useId();
  return (
    <div>
      <label className={LABEL_CLASS} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        className={cn(FIELD_CLASS, disabled && 'opacity-60')}
        defaultValue={String(draftValue(path) ?? '')}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraftValue(path, e.target.value)}
      />
      {hint ? <Hint>{hint}</Hint> : null}
    </div>
  );
};

/** A textarea bound to a draft path, with an optional live character guide. */
export const TextAreaField: React.FC<{
  path: string;
  label: string;
  hint?: React.ReactNode;
  /** A guide, not a limit. */
  cap?: number;
  rows?: number;
}> = ({ path, label, hint, cap, rows = 4 }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const id = useId();
  const [count, setCount] = React.useState(() => String(draftValue(path) ?? '').length);
  return (
    <div>
      <label className={LABEL_CLASS} htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        className={FIELD_CLASS}
        defaultValue={String(draftValue(path) ?? '')}
        onChange={(e) => {
          setDraftValue(path, e.target.value);
          if (cap) setCount(e.target.value.length);
        }}
      />
      {hint || cap ? (
        <Hint>
          {hint}
          {cap ? (
            <span className="text-gray-600 dark:text-gray-400">{`${count} of ${cap} characters`}</span>
          ) : null}
        </Hint>
      ) : null}
    </div>
  );
};

/**
 * A select bound to a draft path. `rerender` marks a select that changes which
 * fields the form shows, so the form repaints on change.
 */
/**
 * AR-381 — the options a select actually offers.
 *
 * The taxonomies in `accountModel` are the prototype's vocabularies; the
 * migrated model stores its own (a `business_type` of `agency_employee`, an
 * `employment_type` of `independent`). A `<select>` whose value is not among
 * its options silently shows the FIRST one, which would make the edit form
 * disagree with the row above it and lose the real value on the next Save. The
 * stored value is carried at the head of the list instead, so the form states
 * what the model holds even where the two vocabularies have not been
 * reconciled yet.
 */
function optionsWithCurrent(
  options: readonly string[],
  current: unknown,
): readonly string[] {
  const value = typeof current === 'string' ? current.trim() : '';
  if (!value || options.includes(value)) return options;
  return [value, ...options];
}

export const SelectField: React.FC<{
  path: string;
  label: string;
  options: readonly string[];
  rerender?: boolean;
}> = ({ path, label, options, rerender }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const id = useId();
  const [, force] = React.useState(0);
  const offered = optionsWithCurrent(options, draftValue(path));
  return (
    <div>
      <label className={LABEL_CLASS} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={FIELD_CLASS}
        defaultValue={String(draftValue(path) ?? '')}
        onChange={(e) => {
          setDraftValue(path, e.target.value);
          if (rerender) force((n) => n + 1);
        }}
      >
        {offered.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
};

/** A select inside a sentence, for a rule that reads as the rule it sets. */
export const InlineSelect: React.FC<{
  path: string;
  options: readonly string[];
  label: string;
}> = ({ path, options, label }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const offered = optionsWithCurrent(options, draftValue(path));
  return (
    <select
      aria-label={label}
      className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      defaultValue={String(draftValue(path) ?? '')}
      onChange={(e) => setDraftValue(path, e.target.value)}
    >
      {offered.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
};

export const CheckField: React.FC<{
  path: string;
  label: string;
  /** Repaint the form: this box changes which fields the form shows. */
  rerender?: boolean;
  onToggle?: () => void;
}> = ({ path, label, rerender, onToggle }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const [checked, setChecked] = React.useState(() => Boolean(draftValue(path)));
  return (
    <label className="mt-2 flex cursor-pointer items-center gap-2 text-[14.5px] text-gray-900 dark:text-gray-100">
      <input
        type="checkbox"
        className="h-4 w-4 flex-none accent-orange-500"
        checked={checked}
        onChange={(e) => {
          setDraftValue(path, e.target.checked);
          setChecked(e.target.checked);
          if (rerender) onToggle?.();
        }}
      />
      {label}
    </label>
  );
};

export const RadioField: React.FC<{
  path: string;
  label: string;
  name: string;
  options: readonly (readonly [string, string])[];
  onChange?: () => void;
}> = ({ path, label, name, options, onChange }) => {
  const { draftValue, setDraftValue } = useAccountRecord();
  const groupId = useId();
  const [value, setValue] = React.useState(() => String(draftValue(path) ?? ''));
  return (
    /* The options each carry their own label; the GROUP needs one too, or the
       question these answer is never announced. */
    <div role="radiogroup" aria-labelledby={groupId}>
      <span id={groupId} className={LABEL_CLASS}>
        {label}
      </span>
      {options.map(([v, l]) => (
        <label
          key={v}
          className="mt-2 flex cursor-pointer items-center gap-2 text-[14.5px] text-gray-900 dark:text-gray-100"
        >
          <input
            type="radio"
            name={name}
            value={v}
            checked={value === v}
            className="h-4 w-4 flex-none accent-orange-500"
            onChange={() => {
              setDraftValue(path, v);
              setValue(v);
              onChange?.();
            }}
          />
          {l}
        </label>
      ))}
    </div>
  );
};

/**
 * A list field edited as chips. Where the path has a taxonomy, the typed text
 * filters the suggestions and only a value the taxonomy carries is accepted;
 * anything else is refused with a line under the field. Adding is always a
 * second, explicit act — picking a suggestion only fills the input.
 */
export const TagField: React.FC<{
  path: string;
  label: string;
  placeholder?: string;
}> = ({ path, label, placeholder }) => {
  const { draftValue, addTag, removeTag, tagQuery, setTagQuery, tagError } =
    useAccountRecord();
  const id = useId();
  const values = (draftValue(path) as string[] | undefined) ?? [];
  const taxonomy = ACCOUNT_TAXONOMY_BY_PATH[path];
  // A field can suggest without fencing: awards autocompletes, but an award
  // WorldVia has never heard of is still the advisor's to claim (D-ACCT-44).
  const suggestFrom = taxonomy ?? ACCOUNT_SUGGESTIONS_BY_PATH[path];
  const typed = tagQuery[path] ?? '';

  // The suggestion list is ours, not a native <datalist>: Chrome's datalist
  // swallows the Enter keydown while its popup is open, and the spec is that
  // Add OR Enter commits. Owning the list also lets "picking a suggestion only
  // fills the input" be true — selecting never adds.
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const suggestions = suggestFrom
    ? suggestFrom
        .filter(
          (x) =>
            !values.includes(x) &&
            (!typed || x.toLowerCase().includes(typed.toLowerCase())),
        )
        .slice(0, 8)
    : [];
  const listOpen = open && suggestions.length > 0;

  const commit = () => {
    addTag(path, typed);
    setOpen(false);
    setActive(-1);
  };

  const fillFrom = (value: string) => {
    setTagQuery(path, value);
    setActive(-1);
  };

  return (
    <div>
      {/* A real label, tied to the input: the placeholder was the only thing
          naming this combobox, which axe accepts but a reader should not have to. */}
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {values.length ? (
          values.map((v, i) => (
            <span
              key={`${v}-${i}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-100 py-0.5 pl-3 pr-1 text-[13.5px] text-gray-700 dark:bg-gray-800 dark:text-gray-200"
            >
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => removeTag(path, i)}
                className="flex h-4 w-4 flex-none items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-700"
              >
                ×
              </button>
            </span>
          ))
        ) : (
          <Muted>Nothing yet.</Muted>
        )}
      </div>
      <div className="relative mt-2 flex gap-1.5">
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            type="text"
            autoComplete="off"
            role="combobox"
            aria-expanded={listOpen}
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={
              active >= 0 ? `${id}-option-${active}` : undefined
            }
            className={cn(FIELD_CLASS, 'w-full py-1.5 text-[14.5px]')}
            value={typed}
            placeholder={placeholder ?? 'Add one'}
            onChange={(e) => {
              setTagQuery(path, e.target.value);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // Let a click on a suggestion land before the list closes.
              blurTimer.current = setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // A highlighted suggestion only fills the field; the next
                // Enter is what commits it.
                if (listOpen && active >= 0) fillFrom(suggestions[active]);
                else commit();
                return;
              }
              if (e.key === 'ArrowDown' && listOpen) {
                e.preventDefault();
                setActive((i) => (i + 1) % suggestions.length);
                return;
              }
              if (e.key === 'ArrowUp' && listOpen) {
                e.preventDefault();
                setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
                return;
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setActive(-1);
              }
            }}
          />
          {listOpen ? (
            <ul
              id={`${id}-list`}
              role="listbox"
              className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            >
              {suggestions.map((sugg, i) => (
                <li key={sugg} id={`${id}-option-${i}`} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => fillFrom(sugg)}
                    className={cn(
                      'block w-full px-3 py-1.5 text-left text-[14px] text-gray-800 dark:text-gray-200',
                      i === active
                        ? 'bg-orange-50 dark:bg-orange-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                    )}
                  >
                    {sugg}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          onClick={commit}
          className="flex-none rounded-md border border-gray-200 bg-white px-3.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          Add
        </button>
      </div>
      {tagError === path ? <Hint error>Choose from the list.</Hint> : null}
    </div>
  );
};

/**
 * An image that is changed by clicking it (D-ACCT-43). The Replace button and
 * its tooltip are gone: the logo is an upload, not something Member Success
 * swaps, so the image itself is the control and it opens the file picker.
 *
 * The chosen image is held in memory and lost on reload — there is no file
 * store yet, and writing a blob URL into the persisted record would leave a
 * broken image behind after a refresh.
 */
export const ImagePlate: React.FC<{
  what: string;
  className?: string;
  onPick: (url: string) => void;
  children: React.ReactNode;
}> = ({ what, className, onPick, children }) => {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => input.current?.click()}
        aria-label={`Change ${what}`}
        title={`Change ${what}`}
        className={cn(
          'group relative flex-none overflow-hidden ring-offset-2 hover:ring-2 hover:ring-orange-400 dark:ring-offset-[#1A2021]',
          className,
        )}
      >
        {children}
        <span className="pointer-events-none absolute inset-0 hidden items-center justify-center gap-1 bg-black/55 text-[11px] font-medium text-white group-hover:flex group-focus-visible:flex">
          <Upload className="h-3 w-3" />
          Change
        </span>
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(URL.createObjectURL(file));
          // Clear it, or picking the same file twice fires no change event.
          e.target.value = '';
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Buttons and doors
// ---------------------------------------------------------------------------

export const SmallButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  primary?: boolean;
}> = ({ children, onClick, disabled, title, primary }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'rounded-md px-3 py-1 text-[13.5px] font-medium',
      primary
        ? 'bg-orange-700 text-white hover:bg-orange-800'
        : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200',
      disabled && 'cursor-not-allowed opacity-60',
    )}
  >
    {children}
  </button>
);

/** A link into another page of the app — routed, so open drafts survive it. */
export const NavLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <Link
    href={to}
    className="cursor-pointer text-orange-700 hover:underline dark:text-orange-400"
  >
    {children}
  </Link>
);

/** An outside address, which opens for real. */
export const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-orange-700 hover:underline dark:text-orange-400"
  >
    {children}
  </a>
);

/**
 * A control the platform has not built yet. It states what happens today
 * instead of offering a room that does not exist.
 */
export const NotYetButton: React.FC<{ children: React.ReactNode; note: string }> = ({
  children,
  note,
}) => (
  <SmallButton disabled title={note}>
    {children}
  </SmallButton>
);
